import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useRef,
} from 'react';
import {useHistory} from 'react-router';
import {usePageTypePathsMap} from './RouterContext';
import {getPageType} from './getCurrentRouteFromUrl';
import {PageTypes} from './types';
import {usePathname} from './useLocation';

type PageTypeCache = Map<string, PageTypes | undefined>;

const PageTypeContext = createContext<PageTypes | undefined>(undefined);
const PageTypeCallbackContext = createContext<() => PageTypes | undefined>(
  () => {
    throw new Error('CurrentPageTypeCallback context is not implemented.');
  },
);

export function PageTypeContextProvider({children}: PropsWithChildren) {
  const pageTypePerUrlCache = useRef<PageTypeCache>(new Map());
  const pathname = usePathname();
  const pageTypePathsMap = usePageTypePathsMap();

  const getPageTypeByPathname = useCallback(
    (pathname: string) => {
      const currentPageType =
        pageTypePerUrlCache.current.get(pathname) ??
        getPageType(pageTypePathsMap, pathname);
      if (!pageTypePerUrlCache.current.has(pathname)) {
        pageTypePerUrlCache.current.set(pathname, currentPageType);
      }
      return currentPageType;
    },
    [pageTypePathsMap],
  );

  const history = useHistory();
  const currentPageTypeCallback = useCallback(
    () => getPageTypeByPathname(history.location.pathname),
    [history, getPageTypeByPathname],
  );

  return (
    <PageTypeContext.Provider value={getPageTypeByPathname(pathname)}>
      <PageTypeCallbackContext.Provider value={currentPageTypeCallback}>
        {children}
      </PageTypeCallbackContext.Provider>
    </PageTypeContext.Provider>
  );
}

export const useCurrentPageTypeCallback = () =>
  useContext(PageTypeCallbackContext);

/**
 * Returns the current page type and re-renders the component in case the page type changes.
 */
export const useCurrentPageType = (): PageTypes | undefined => {
  return useContext(PageTypeContext);
};
